import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { ListingResponse } from "@hybrid-escrow/shared";
import { apiClient } from "../lib/api-client";
import { queryKeys } from "../lib/queryClient";
import { describeError } from "./States";
import { ListingCover } from "./ListingCover";

const MAX_BYTES = 2 * 1024 * 1024;
const ACCEPTED = ["image/png", "image/jpeg", "image/webp"];

/** Seller-side cover image control on the listing edit page. The server re-checks size and
 *  sniffs the real file type; the checks here only save a round trip. */
export function CoverImageManager({ listing }: { listing: ListingResponse }) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: queryKeys.sellerListings });
    await queryClient.invalidateQueries({ queryKey: queryKeys.listing(listing.id) });
  }

  async function onPick(file: File | undefined) {
    if (!file) return;
    setError(null);
    if (!ACCEPTED.includes(file.type)) {
      setError("Use a PNG, JPEG or WebP image.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("That image is over 2 MB — please use a smaller one.");
      return;
    }
    setBusy(true);
    try {
      await apiClient.uploadListingCover(listing.id, file);
      await refresh();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function onRemove() {
    setError(null);
    setBusy(true);
    try {
      await apiClient.removeListingCover(listing.id);
      await refresh();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card stack">
      <h2>Cover image</h2>
      <p className="text-sm muted">
        Optional. Shown on the browse page and your listing. PNG, JPEG or WebP, up to 2 MB.
      </p>
      <ListingCover listing={listing} className="listing-cover" />
      <div className="row">
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED.join(",")}
          disabled={busy}
          onChange={(e) => void onPick(e.target.files?.[0])}
          aria-label="Cover image file"
        />
        {listing.hasCoverImage && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={busy}
            onClick={() => void onRemove()}
          >
            Remove
          </button>
        )}
      </div>
      {busy && <p className="text-sm muted">Working…</p>}
      {error && (
        <p className="field-error-text" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
